import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useOrderData } from 'hooks/orderForms/useOrderData';
import { logError } from 'services/logging/logging';
import { GenericError } from 'support/helpers/errors/errors';
import { OrderData } from 'support/helpers/orderForms/orderForms';
import { OrderFormCategoryDTO } from 'support/types/dtos';

const initialData: OrderData = {
  products: {},
};

export const useOrderFormWithValidatedStorage = (categories?: Array<OrderFormCategoryDTO>) => {
  const { supplierSlug } = useParams<{ supplierSlug: string }>();
  const [orderData, setOrderData] = useOrderData(supplierSlug || '', initialData);

  const formMethods = useForm<OrderData>({
    defaultValues: orderData,
  });

  const { watch, reset } = formMethods;

  useEffect(() => {
    if (!categories) {
      return;
    }

    const availableProductIds = new Set(
      categories.flatMap((category) => category.products?.map((product) => product.id) || []),
    );

    const savedProductIds = Object.keys(orderData.products);
    const unavailableProducts = savedProductIds.filter((id) => !availableProductIds.has(id));

    if (unavailableProducts.length > 0) {
      logError(
        new GenericError('Order Forms: Saved products are no longer available', {
          supplierSlug,
          unavailableProducts,
          savedProductIds,
          availableProductIds: Array.from(availableProductIds),
        }),
      );
      reset(initialData);
    }
  }, [categories, orderData, supplierSlug, reset]);

  useEffect(() => {
    const subscription = watch((value) => {
      setOrderData((prevOrderData) => {
        const updatedOrderData = {
          ...prevOrderData,
          ...value,
          parties: prevOrderData?.parties,
        };

        return updatedOrderData as OrderData;
      });
    });
    return () => subscription.unsubscribe();
  }, [watch, setOrderData]);

  return formMethods;
};
